// Import constants
const {
    formatCurrency
} = require('../constants.js');

// Define component
const ColourMatches = {
    props: {
        postItemToCart: {
            type: Function,
            required: true
        },
        addToCartLoading: {
            type: Boolean,
            required: true
        }
    },

    setup(props) {
        const { ref, computed } = Vue;
        const { useStore } = Vuex;

        const store = useStore();
        const quantities = ref({});

        const accessoryDescriptionName = computed(() => store.state.accessoryDescriptionName);

        const loading = ref(null);

        const colourMatches = computed(() => {
            if (vueAppData.cart.products && vueAppData.cart.products.length > 0) {
                const matches = vueAppData.cart.products.filter(product => product.matched_accessories.some(accessory => accessory.accessory_id == store.state.productId)
                );

                // Initialize quantities for new matches if they don't exist
                matches.forEach(match => {
                    match.accessory_colour = match.matched_accessories.find(accessory => accessory.accessory_id == store.state.productId).accessory_colour;
                    if (!(match.cart_item_key in quantities.value)) {
                        quantities.value[match.cart_item_key] = 0;
                    }
                });

                return matches;
            }
            return [];
        });

        const addItemToCart = async (quantity, accessoryOption, index) => {
            if (quantity == 0 || quantity == null) {
                return;
            }

            const formData = new FormData();
            formData.append("action", "add_main_and_upsell_to_cart");
            formData.append("main_product_id", store.state.productId);
            formData.append("main_quantity", quantity);
            formData.append("weldrod_option", accessoryOption);

            loading.value = index;
            await props.postItemToCart(formData);
            loading.value = null;
        };

        return {
            accessoryDescriptionName,
            quantities,
            loading,
            colourMatches,
            addItemToCart,
            formatCurrency
        };
    },

    template: `
        <template v-if="colourMatches.length > 0">
            <h2 class="text-shades-900">Based on items in your cart</h2>
            <p class="p-0 m-0 mb-3 text-sm text-shades-500">
                We've matched your {{ accessoryDescriptionName }} so you don't have to.
            </p>
            <div class="p-3 mb-5 rounded-lg border border-solid border-shades-100 bg-shades-50 last-of-type:mb-0"
                v-for="(matchProduct, index) in colourMatches" 
                :key="index">
                
                <!-- Product Info Row -->
                <div class="flex items-start gap-3 mb-3">
                    <div class="w-14 h-14 overflow-hidden rounded-md bg-shades-200 shrink-0">
                        <img class="w-full h-full object-cover" :src="matchProduct.image_url" :alt="matchProduct.product_name">
                    </div>
                    <div class="flex-1 min-w-0">
                        <div class="text-sm font-medium text-shades-800 line-clamp-2">{{ matchProduct.product_name }}</div>
                        <div class="mt-1 text-sm text-shades-600">
                            <span class="font-semibold text-shades-700">
                                {{ accessoryDescriptionName.charAt(0).toUpperCase() +
                                    accessoryDescriptionName.slice(1) }} to match:
                            </span> 
                            <span class="text-shades-800">{{ matchProduct.accessory_colour }}</span>
                        </div>
                    </div>
                </div>

                <!-- Quantity & Add Button Row -->
                <div class="flex items-center gap-2">
                    <div class="flex items-center border border-solid border-shades-200 rounded-lg bg-white overflow-hidden">
                        <button 
                            @click="quantities[matchProduct.cart_item_key] = Math.max(0, (quantities[matchProduct.cart_item_key] || 0) - 1)"
                            class="w-9 h-9 flex items-center justify-center text-shades-500 hover:bg-shades-50 transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/></svg>
                        </button>
                        <input type="number" 
                            class="w-12 h-9 text-center text-sm font-medium border-0 bg-transparent focus:outline-none focus:ring-0"
                            v-model="quantities[matchProduct.cart_item_key]" 
                            min="0">
                        <button 
                            @click="quantities[matchProduct.cart_item_key] = (quantities[matchProduct.cart_item_key] || 0) + 1"
                            class="w-9 h-9 flex items-center justify-center text-shades-500 hover:bg-shades-50 transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>
                        </button>
                    </div>
                    <button @click="addItemToCart(quantities[matchProduct.cart_item_key], matchProduct.accessory_colour, index)"
                        class="flex-1 h-9 flex items-center justify-center gap-2 px-4 text-sm font-medium text-white bg-shades-800 rounded-lg hover:bg-shades-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        :disabled="quantities[matchProduct.cart_item_key] == 0 || quantities[matchProduct.cart_item_key] == null">
                        <svg v-if="loading == index" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"
                            class="w-4 h-4 animate-spin">
                            <path fill="currentColor" opacity=".4"
                                d="M0 256C0 397.4 114.6 512 256 512c94.7 0 177.5-51.5 221.7-128l-55.4-32c-33.2 57.4-95.2 96-166.3 96C150 448 64 362 64 256S150 64 256 64l0-64C114.6 0 0 114.6 0 256z" />
                            <path fill="currentColor"
                                d="M477.7 384c21.8-37.7 34.3-81.4 34.3-128C512 114.6 397.4 0 256 0l0 64c106 0 192 86 192 192c0 35-9.4 67.8-25.7 96l55.4 32z" />
                        </svg>
                        <span v-else>Add to Cart</span>
                    </button>
                </div>
            </div>

            <div class="flex items-center justify-center w-full mb-5">
                <span class="or-line"></span>
                <span class="px-5 text-lg font-bold text-shades-500">or</span>
                <span class="or-line"></span>
            </div>
        </template>
    `
};

// Export the component using module.exports
module.exports = { ColourMatches }; 